import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAppStore } from '@/lib/store'

interface CalendarDay {
  date: string
  price_uah: number
  available: boolean
  available_count: number
}

interface Props {
  roomTypeId: number
  checkIn: string
  checkOut: string
  quantity?: number
  onChange: (checkIn: string, checkOut: string) => void
  respectAvailability?: boolean
  tone?: 'dark' | 'light'
}

const HORIZON_DAYS = 120

const WEEKDAY_LABELS: Record<'uk' | 'en' | 'ru', string[]> = {
  uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
}

const MONTH_LABELS: Record<'uk' | 'en' | 'ru', string[]> = {
  uk: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
}

export function DateRangeCalendar({
  roomTypeId,
  checkIn,
  checkOut,
  quantity = 1,
  onChange,
  respectAvailability = true,
  tone = 'dark',
}: Props) {
  const lang = useAppStore((s) => s.lang)
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()))
  const isLight = tone === 'light'

  const todayStr = format(new Date(), 'yyyy-MM-dd')

  const { data: days, isLoading } = useQuery<CalendarDay[]>({
    queryKey: ['lodging-calendar', roomTypeId],
    queryFn: () =>
      api.get<CalendarDay[]>(
        `/lodging/room-types/${roomTypeId}/calendar?start=${todayStr}&days=${HORIZON_DAYS}`,
      ),
    staleTime: 60_000,
  })

  const dayMap = useMemo(() => {
    const m = new Map<string, CalendarDay>()
    days?.forEach((d) => m.set(d.date, d))
    return m
  }, [days])

  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 })
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 })
    return eachDayOfInterval({ start, end })
  }, [viewMonth])

  function isRangeAvailable(startStr: string, endStr: string): boolean {
    if (!respectAvailability) return true
    try {
      const nights = eachDayOfInterval({
        start: parseISO(startStr),
        end: addDays(parseISO(endStr), -1),
      })
      return nights.every((night) => {
        const dStr = format(night, 'yyyy-MM-dd')
        const info = dayMap.get(dStr)
        return Boolean(info && info.available_count >= quantity)
      })
    } catch {
      return false
    }
  }

  function handleDayClick(dateStr: string) {
    if (dateStr < todayStr) return
    const info = dayMap.get(dateStr)
    const isDayAvailableForCheckIn = !respectAvailability || Boolean(info && info.available_count >= quantity)

    if (!checkIn || (checkIn && checkOut) || dateStr <= checkIn) {
      if (!isDayAvailableForCheckIn) return
      onChange(dateStr, '')
      return
    }

    if (isRangeAvailable(checkIn, dateStr)) {
      onChange(checkIn, dateStr)
    } else if (isDayAvailableForCheckIn) {
      onChange(dateStr, '')
    }
  }

  const horizonEndStr = format(addDays(new Date(), HORIZON_DAYS), 'yyyy-MM-dd')

  return (
    <div className={`rounded-xl border p-3 ${isLight ? 'border-slate-200 bg-white' : 'border-slate-800 bg-slate-950'}`}>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, -1))}
          disabled={format(viewMonth, 'yyyy-MM') <= format(new Date(), 'yyyy-MM')}
          className={`rounded-lg p-1 disabled:opacity-30 ${
            isLight
              ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100'
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-slate-200'}`}>
          {MONTH_LABELS[lang][viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </p>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          disabled={format(viewMonth, 'yyyy-MM') >= format(new Date(horizonEndStr), 'yyyy-MM')}
          className={`rounded-lg p-1 disabled:opacity-30 ${
            isLight
              ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100'
          }`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className={`grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
        {WEEKDAY_LABELS[lang].map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {gridDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd')
          const inMonth = format(day, 'yyyy-MM') === format(viewMonth, 'yyyy-MM')
          const info = dayMap.get(dateStr)
          const isPast = dateStr < todayStr
          const isDayAvailableForCheckIn = !respectAvailability || Boolean(info && info.available_count >= quantity)
          const isCheckOutCandidate = Boolean(checkIn && !checkOut && dateStr > checkIn)
          const canCheckOut = isCheckOutCandidate && isRangeAvailable(checkIn, dateStr)
          const isUnavailable = isPast || (!canCheckOut && !isDayAvailableForCheckIn)
          const isCheckIn = dateStr === checkIn
          const isCheckOut = dateStr === checkOut
          const isInRange = Boolean(checkIn && checkOut && dateStr > checkIn && dateStr < checkOut)

          return (
            <button
              key={dateStr}
              data-date={dateStr}
              type="button"
              disabled={isUnavailable || !inMonth}
              onClick={() => handleDayClick(dateStr)}
              className={`flex flex-col items-center rounded-lg py-1 text-xs transition ${
                !inMonth
                  ? 'invisible'
                  : isUnavailable
                    ? `cursor-not-allowed line-through ${isLight ? 'text-slate-300' : 'text-slate-700'}`
                    : isCheckIn || isCheckOut
                      ? 'bg-indigo-600 font-bold text-white'
                    : isInRange
                        ? isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-600/20 text-indigo-200'
                        : isLight ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-200 hover:bg-slate-800'
              }`}
            >
              <span>{day.getDate()}</span>
              {inMonth && info && !isUnavailable && (
                <>
                  <span
                    className={`text-[9px] leading-none ${
                      isCheckIn || isCheckOut ? 'text-white/80' : isLight ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {info.price_uah}
                  </span>
                  <span
                    className={`text-[8px] leading-none ${
                      isCheckIn || isCheckOut
                        ? 'text-white/70'
                        : info.available_count <= 1
                          ? 'text-amber-400'
                          : 'text-emerald-400'
                    }`}
                  >
                    ×{info.available_count}
                  </span>
                </>
              )}
            </button>
          )
        })}
      </div>

      {isLoading && <p className={`mt-2 text-center text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>…</p>}
    </div>
  )
}
